/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-10 15:00:02
 */
/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印页面
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-19 16:44:39
 */

import React, { useEffect, useRef, useState } from 'react'
import { Button, Layout, Modal, message } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppSelector, useAppDispatch, useGroupID } from '../../../app/hooks'
import { folderDetailPath } from '../../../utils/aiFolderClassify'
import { clearPhotoPickerPhotos } from '../../../actions/photoPicker'
import { deletePhoto } from '../../../actions/photo'
import {
  PhotoItem,
  WatermarkBatchContextProvider,
  WatermarkExtractionStatus
} from './context/WatermarkBatchContext'
import { useWatermarkBatchContext } from './context/WatermarkBatchContext'
import ImageList from './components/ImageList/ImageList'
import ImagePreview from './components/ImagePreview/ImagePreview'
import WatermarkEditPanel from './components/WatermarkEditPanel/WatermarkEditPanel'
import WatermarkExtractionModal from './components/ImageList/WatermarkExtractionModal'
import SurveyModal from './components/SurveyModal'
import SurveyButton from './components/SurveyButton'
import UsageGuide from './components/UsageGuide'
import DownloadProgressModal from './components/Download/DownloadProgressModal'
import UploadEditedPhotosModal from './components/Upload/UploadEditedPhotosModal'
import FullscreenPreviewModal from './components/Preview/FullscreenPreviewModal'
import './index.scss'
import ContactUS from '../../components/ContactUS'
import contact_us_icon from '../../../images/contact_us.png'
import {
  useWatermarkBatchEditorTrack,
  calculatePhotoStats
} from './hooks/useWatermarkBatchEditorTrack'

import PageHeader from '@/components/PageHeader'
import AntdProvider from '@/components/AntdProvider'
const { Content } = Layout

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface WatermarkBatchEditorProps {}

// 内部组件，使用Context
const WatermarkBatchEditorContent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const groupID = useGroupID()
  const {
    photoList,
    removePhotosBySeqs,
    updatePhotos,
    watermarkCnt,
    currentPhoto,
    isExtracting,
    extractingPhotoCount
  } = useWatermarkBatchContext()
  const prevPathnameRef = useRef<string>(location.pathname)

  // 使用埋点 hook
  const { trackWatermarkBatchEditor } = useWatermarkBatchEditorTrack()

  // 从 Context 获取问卷、上传、下载进度、全屏预览相关状态与方法
  const {
    surveyModalVisible,
    hideSurveyModal,
    uploadModalVisible,
    setUploadModalVisible,
    downloadProgress,
    setDownloadProgress,
    fullscreenPreview,
    closeFullscreenPreview,
    invokeUploadConfirm,
    invokeDownloadAll
  } = useWatermarkBatchContext()

  const renderUploadDeleteMessage = (
    uploadedCount: number,
    deletedCount: number,
    uploadFailedCount: number,
    deleteFailedCount: number,
    folderID: number,
    folderName?: string
  ) => (
    <span>
      已上传{uploadedCount}张并删除{deletedCount}张原图
      <span
        role="button"
        tabIndex={0}
        onClick={() => navigate(folderDetailPath(folderID))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            navigate(folderDetailPath(folderID))
          }
        }}
        style={{
          cursor: 'pointer',
          color: '#1890ff',
          textDecoration: 'underline',
          marginLeft: 4
        }}
      >
        {folderName || '文件夹'}
      </span>
      {(uploadFailedCount > 0 || deleteFailedCount > 0) && (
        <span style={{ marginLeft: 8 }}>
          上传失败{uploadFailedCount}张，删除失败{deleteFailedCount}张
        </span>
      )}
    </span>
  )

  const renderUploadOnlyMessage = (
    uploadedCount: number,
    uploadFailedCount: number,
    folderID: number,
    folderName?: string
  ) => (
    <span>
      已上传{uploadedCount}张
      <span
        role="button"
        tabIndex={0}
        onClick={() => navigate(folderDetailPath(folderID))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            navigate(folderDetailPath(folderID))
          }
        }}
        style={{
          cursor: 'pointer',
          color: '#1890ff',
          textDecoration: 'underline',
          marginLeft: 4
        }}
      >
        {folderName || '文件夹'}
      </span>
      {uploadFailedCount > 0 && (
        <span style={{ marginLeft: 8 }}>
          上传失败{uploadFailedCount}张，未删除原图
        </span>
      )}
    </span>
  )

  // 使用说明引导状态
  const [usageGuideRun, setUsageGuideRun] = useState(false)

  // 检查是否已显示过引导（从本地缓存读取）
  const hasShownGuide = useRef<boolean>(
    typeof window !== 'undefined' && window.localStorage
      ? localStorage.getItem('watermark-batch-editor-guide-shown') === 'true'
      : false
  )

  // 获取用户和团队信息
  const user = useAppSelector((state) => state.main.user)
  const group = useAppSelector((state) => state.group)

  // 生成带参数的问卷链接
  const getSurveyUrl = () => {
    const userID = user?.user_id || ''
    const nickname = user?.nickname || ''
    const inviteID = group ? group.invite_id || group.groupId || '' : ''
    const groupName = group ? group.groupName || '' : ''
    return `https://alidocs.dingtalk.com/notable/share/form/v013BMqYybKYzKb6qwZ_hERWDMS_WyPvuh8?prefill_UserID=${encodeURIComponent(
      userID || ''
    )}&prefill_%E5%A7%93%E5%90%8D=${encodeURIComponent(
      nickname || ''
    )}&prefill_%E5%9B%A2%E9%98%9F%E5%8F%B7=${encodeURIComponent(
      inviteID || ''
    )}&prefill_%E5%9B%A2%E9%98%9F%E5%90%8D=${encodeURIComponent(
      groupName || ''
    )}`
  }

  // 计算照片统计
  const { allPhotoCount, uneditedCount, failedExtractionCount } =
    calculatePhotoStats(photoList)

  // 从photoPicker中获取选中的照片和from字段
  const selectedPhotosFromPicker = useAppSelector((state) => {
    // 检查photoPicker中的from字段是否为batch-editor
    if (state.photoPicker.from === 'batch-editor') {
      const photos = state.photoPicker.photos
      // 直接返回数组
      return Array.isArray(photos) ? photos : []
    }

    // 否则返回空数组
    return []
  })

  const photoPickerFrom = useAppSelector((state) => state.photoPicker.from)
  const photoPickerFromRef = useRef(photoPickerFrom)
  const photoListRef = useRef<PhotoItem[]>(photoList)
  const lastPickerSignatureRef = useRef<string>('')

  // 保持 ref 与最新值同步
  useEffect(() => {
    photoPickerFromRef.current = photoPickerFrom
  }, [photoPickerFrom])
  useEffect(() => {
    photoListRef.current = photoList
  }, [photoList])

  // 监听路由变化，当离开 batchEditor 页面时清空已选照片
  useEffect(() => {
    const currentPathname = location.pathname
    const prevPathname = prevPathnameRef.current

    // 如果从 batchEditor 页面离开，且不是跳转到 add 页面，则清空已选照片
    if (
      prevPathname === '/watermark/batchEditor' &&
      currentPathname !== '/watermark/batchEditor' &&
      currentPathname !== '/watermark/batchEditor/add'
    ) {
      // 使用 ref 获取最新的 from 值，避免闭包问题
      if (photoPickerFromRef.current === 'batch-editor') {
        dispatch(clearPhotoPickerPhotos())
      }
    }

    // 更新路径引用
    prevPathnameRef.current = currentPathname
  }, [location.pathname, dispatch])

  // 组件卸载时清空已选照片（但排除跳转到 add 页面的情况）
  useEffect(() => {
    return () => {
      // 检查下一个路径是否是 add 页面，如果是就不清空
      const nextPathname = window.location.pathname
      if (nextPathname === '/watermark/batchEditor/add') {
        return
      }

      // 使用 ref 获取最新的 from 值，避免闭包问题
      if (photoPickerFromRef.current === 'batch-editor') {
        dispatch(clearPhotoPickerPhotos())
      }
    }
  }, [dispatch])

  // 进入页面埋点（只在首次进入时上报一次）
  const hasEnteredPageRef = useRef<boolean>(false)
  useEffect(() => {
    // 只在首次进入页面时上报，避免在应用水印后数据变化导致重复上报
    if (!hasEnteredPageRef.current) {
      trackWatermarkBatchEditor({
        actionType: 'enterPage',
        number1: watermarkCnt,
        number2: allPhotoCount,
        number3: uneditedCount,
        number4: failedExtractionCount,
        photoNum: allPhotoCount,
        WatermarkID: currentPhoto?.watermarkID
      })
      hasEnteredPageRef.current = true
    }
  }, [
    trackWatermarkBatchEditor,
    watermarkCnt,
    allPhotoCount,
    uneditedCount,
    failedExtractionCount,
    currentPhoto?.watermarkID
  ])

  // 选择照片后，自动处理照片
  useEffect(() => {
    const signature = (selectedPhotosFromPicker || [])
      .map((p: any) => String(p.seq))
      .sort()
      .join(',')
    if (signature === lastPickerSignatureRef.current) {
      return
    }
    lastPickerSignatureRef.current = signature

    // 处理从photoPicker传递过来的照片
    if (selectedPhotosFromPicker && selectedPhotosFromPicker.length > 0) {
      const currentPhotos = photoListRef.current
      // 过滤掉已经在 Context 中存在的照片，避免重复添加
      const currentPhotoListSet = new Set(
        currentPhotos.map((p: PhotoItem) => p.smallURL)
      )
      const newPhotos = selectedPhotosFromPicker.filter(
        (p: any) => !currentPhotoListSet.has(p.smallURL)
      )

      if (newPhotos.length > 0) {
        console.warn('batch-editor: sync from photoPicker', {
          selectedCount: selectedPhotosFromPicker.length,
          currentCount: currentPhotos.length,
          newCount: newPhotos.length,
          newSeqs: newPhotos.map((p: any) => p.seq),
          sampleCurrentSeqs: currentPhotos.slice(0, 10).map((p) => p.seq)
        })
      }

      if (newPhotos.length > 0) {
        // 插入到updatePhotos中
        updatePhotos(newPhotos as PhotoItem[])
      }
    }
  }, [selectedPhotosFromPicker, updatePhotos])

  // 当照片列表有数据且首次提取完成时，自动显示引导
  useEffect(() => {
    // 条件：照片列表有数据 && 不在提取中 && 提取完成 && 未显示过引导
    const hasPhotos = photoList.length > 0
    const extractionCompleted = !isExtracting && extractingPhotoCount === 0
    const shouldShowGuide = !hasShownGuide.current

    if (hasPhotos && extractionCompleted && shouldShowGuide) {
      // 延迟一下，确保UI已经更新
      const timer = setTimeout(() => {
        setUsageGuideRun(true)
      }, 500)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [photoList.length, isExtracting, extractingPhotoCount])

  // 处理返回按钮点击
  const handleBack = () => {
    // 埋点：返回
    trackWatermarkBatchEditor({
      actionType: 'back',
      number1: watermarkCnt,
      number2: allPhotoCount,
      number3: uneditedCount,
      number4: failedExtractionCount,
      photoNum: allPhotoCount,
      WatermarkID: currentPhoto?.watermarkID
    })

    // 检查是否有编辑成功的照片
    const hasEditedPhotos = photoList.some(
      (photo) =>
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED
    )
    // 是否存在未下载的已编辑照片（仅当存在时展示「上传到文件夹 / 下载」两个按钮）
    const hasUndownloadedEditedPhotos = photoList.some(
      (p) =>
        p.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED &&
        !p.isDownloaded
    )

    if (hasEditedPhotos) {
      // 如果有编辑成功的照片，显示确认弹窗
      Modal.confirm({
        title: '确认离开',
        content: (
          <div>
            离开页面将<strong>清空已编辑照片</strong>，是否确认离开？
            {hasUndownloadedEditedPhotos && (
              <>
                <div style={{ marginTop: 16, marginBottom: 12 }}>
                  <span style={{ color: 'rgba(0,0,0,0.65)', fontSize: 14 }}>
                    已编辑照片
                    <span style={{ color: '#fa8c16' }}>不会自动同步到相册</span>
                    ，可先：
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap'
                  }}
                >
                  <Button
                    type="link"
                    style={{
                      color: '#1890ff',
                      padding: '4px 12px',
                      height: 32,
                      border: '1px solid rgba(24,144,255,0.3)',
                      borderRadius: 6
                    }}
                    onClick={() => {
                      trackWatermarkBatchEditor({
                        actionType: 'uploadToFolder',
                        number1: watermarkCnt
                      })
                      Modal.destroyAll()
                      setUploadModalVisible(true)
                    }}
                  >
                    上传到文件夹
                  </Button>
                  <Button
                    type="link"
                    style={{
                      color: '#1890ff',
                      padding: '4px 12px',
                      height: 32,
                      border: '1px solid rgba(24,144,255,0.3)',
                      borderRadius: 6
                    }}
                    onClick={() => {
                      Modal.destroyAll()
                      if (invokeDownloadAll) invokeDownloadAll()
                    }}
                  >
                    下载全部已编辑
                  </Button>
                </div>
              </>
            )}
          </div>
        ),
        okText: '确认离开',
        cancelText: '取消',
        onOk: () => {
          // 用户确认后，清空已选照片并返回到全部照片页面
          if (photoPickerFrom === 'batch-editor') {
            dispatch(clearPhotoPickerPhotos())
          }
          navigate('/photo')
        }
      })
    } else {
      // 没有编辑成功的照片，直接返回到全部照片页面
      if (photoPickerFrom === 'batch-editor') {
        dispatch(clearPhotoPickerPhotos())
      }
      navigate('/photo')
    }
  }

  return (
    <Layout className="watermarkBatchEditor">
      <PageHeader
        ghost={false}
        onBack={handleBack}
        title="批量编辑水印"
        extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* 使用说明按钮 - 仅在当前页面有已选择照片时显示 */}
            {photoList.length > 0 && (
              <Button
                type="default"
                icon={<QuestionCircleOutlined />}
                onClick={() => {
                  // 清除缓存，允许重新显示引导
                  if (typeof window !== 'undefined' && window.localStorage) {
                    localStorage.removeItem(
                      'watermark-batch-editor-guide-shown'
                    )
                    hasShownGuide.current = false
                  }
                  setUsageGuideRun(true)
                  // 埋点：点击使用说明
                  trackWatermarkBatchEditor({
                    actionType: 'clickUsageGuide',
                    number1: watermarkCnt,
                    number2: allPhotoCount,
                    number3: uneditedCount,
                    number4: failedExtractionCount,
                    photoNum: allPhotoCount,
                    WatermarkID: currentPhoto?.watermarkID
                  })
                }}
              >
                使用说明
              </Button>
            )}
            {/* 问卷入口 */}
            <SurveyButton
              surveyUrl={getSurveyUrl()}
              onClick={() => {
                // 埋点：点击问卷按钮
                trackWatermarkBatchEditor({
                  actionType: 'clickSurvey',
                  number1: watermarkCnt,
                  number2: allPhotoCount,
                  number3: uneditedCount,
                  number4: failedExtractionCount,
                  photoNum: allPhotoCount,
                  WatermarkID: currentPhoto?.watermarkID
                })
              }}
            />
            {/* 联系客服 */}
            <ContactUS fromPage="批量编辑水印">
              <span
                className="contact-item"
                onClick={() => {
                  // 埋点：联系客服
                  trackWatermarkBatchEditor({
                    actionType: 'contactUs',
                    number1: watermarkCnt,
                    number2: allPhotoCount,
                    number3: uneditedCount,
                    number4: failedExtractionCount,
                    photoNum: allPhotoCount,
                    WatermarkID: currentPhoto?.watermarkID
                  })
                }}
              >
                <img style={{ width: '105px' }} src={contact_us_icon} />
              </span>
            </ContactUS>
          </div>
        }
      />
      <Content className="content">
        <div className="mainContent">
          {/* 左侧图片列表 */}
          <div className="leftPanel">
            <ImageList />
          </div>

          {/* 中间图片预览 */}
          <div className="centerPanel">
            <ImagePreview />
          </div>

          {/* 右侧水印编辑 */}
          <div className="rightPanel">
            <WatermarkEditPanel />
          </div>
        </div>
      </Content>

      {/* 水印提取进度弹窗 */}
      <WatermarkExtractionModal
        visible={isExtracting}
        photoCount={extractingPhotoCount}
      />

      {/* 问卷入口弹窗 */}
      <SurveyModal
        visible={surveyModalVisible}
        onCancel={hideSurveyModal}
        onParticipate={() => {
          /* noop */
        }}
        surveyUrl={getSurveyUrl()}
      />

      {/* 下载进度弹窗（根挂载，由 ImagePreview/ImageList 通过 Context 触发） */}
      <DownloadProgressModal
        visible={downloadProgress.visible}
        total={downloadProgress.total}
        current={downloadProgress.current}
        title={downloadProgress.title}
        onCancel={() =>
          setDownloadProgress({
            visible: false,
            current: 0,
            total: 0
          })
        }
      />

      {/* 上传已编辑照片弹窗（根挂载，由 ImagePreview 通过 Context 触发） */}
      <UploadEditedPhotosModal
        visible={uploadModalVisible}
        photos={photoList}
        onCancel={() => setUploadModalVisible(false)}
        onConfirm={(selectedPhotos, folderID, folderName) => {
          setUploadModalVisible(false)
          invokeUploadConfirm(selectedPhotos, folderID, folderName)
        }}
        onUploadAndDelete={async (selectedPhotos, folderID, folderName) => {
          setUploadModalVisible(false)
          const uploadedPhotos = await invokeUploadConfirm(
            selectedPhotos,
            folderID,
            folderName,
            { suppressToast: true }
          )
          const uploadFailedCount =
            selectedPhotos.length - (uploadedPhotos?.length || 0)

          // 全部上传失败：不删除任何原图
          if (!uploadedPhotos || uploadedPhotos.length === 0) {
            message.warning('上传失败，未删除原图')
            return
          }

          // 只删除上传成功的照片的原图
          if (!groupID) {
            message.warning('上传完成，但未能删除原图')
            return
          }
          const photoSeqs = uploadedPhotos.map((photo) => photo.seq)
          try {
            const data = await dispatch(
              deletePhoto({
                group_id: groupID,
                photo_list: photoSeqs
              })
            )
            if (data?.status === 0) {
              const deletedSeqs =
                Array.isArray(data?.realDeletePhotoList) &&
                data.realDeletePhotoList.length > 0
                  ? data.realDeletePhotoList
                  : photoSeqs
              const deletedSeqSet = new Set(
                deletedSeqs.map((seq) => String(seq))
              )
              let deletedPhotos = uploadedPhotos.filter((photo) =>
                deletedSeqSet.has(String(photo.seq))
              )
              if (deletedPhotos.length === 0 && uploadedPhotos.length > 0) {
                deletedPhotos = uploadedPhotos
              }
              const deleteFailedCount =
                uploadedPhotos.length - deletedPhotos.length
              if (deletedPhotos.length > 0) {
                removePhotosBySeqs(deletedPhotos.map((photo) => photo.seq))
              }

              // 部分上传失败：提示成功数和失败数，失败照片的原图未被删除
              if (uploadFailedCount > 0 || deleteFailedCount > 0) {
                message.warning({
                  content: renderUploadDeleteMessage(
                    uploadedPhotos.length,
                    deletedPhotos.length,
                    uploadFailedCount,
                    deleteFailedCount,
                    folderID,
                    folderName
                  ),
                  duration: 4
                })
              } else {
                message.success({
                  content: renderUploadDeleteMessage(
                    uploadedPhotos.length,
                    deletedPhotos.length,
                    0,
                    0,
                    folderID,
                    folderName
                  ),
                  duration: 4
                })
              }
            }
          } catch (error) {
            // deletePhoto 内部已做错误提示，这里无需重复提示
          }
        }}
      />

      {/* 全屏预览弹窗（根挂载，由 WatermarkProcessModal 等通过 Context 触发） */}
      <FullscreenPreviewModal
        visible={fullscreenPreview.visible}
        imageList={fullscreenPreview.imageList}
        index={fullscreenPreview.index}
        photoType={fullscreenPreview.photoType}
        failedPhotos={fullscreenPreview.failedPhotos}
        onClose={closeFullscreenPreview}
      />

      {/* 使用说明引导 */}
      <UsageGuide
        run={usageGuideRun}
        onComplete={() => {
          setUsageGuideRun(false)
          // 更新内存中的标记，与 localStorage 保持一致
          hasShownGuide.current = true
        }}
        onFinish={() => {
          // 埋点：完成使用说明引导
          trackWatermarkBatchEditor({
            actionType: 'completeUsageGuide',
            number1: watermarkCnt,
            number2: allPhotoCount,
            number3: uneditedCount,
            number4: failedExtractionCount,
            photoNum: allPhotoCount,
            WatermarkID: currentPhoto?.watermarkID
          })
        }}
        onSkip={() => {
          // 埋点：跳过使用说明引导
          trackWatermarkBatchEditor({
            actionType: 'skipUsageGuide',
            number1: watermarkCnt,
            number2: allPhotoCount,
            number3: uneditedCount,
            number4: failedExtractionCount,
            photoNum: allPhotoCount,
            WatermarkID: currentPhoto?.watermarkID
          })
        }}
      />
    </Layout>
  )
}

const WatermarkBatchEditor: React.FC<WatermarkBatchEditorProps> = () => {
  return (
    <AntdProvider>
      <WatermarkBatchContextProvider>
        <WatermarkBatchEditorContent />
      </WatermarkBatchContextProvider>
    </AntdProvider>
  )
}

export default WatermarkBatchEditor
